import { type IEvent } from 'clients/PlatformApi';
import { useCallback, useState } from 'react';
import { type LoadedSegments } from '../VideoPlayer/controller/PlaybackController';

export interface OpenSavePanelHandler {
  (event: IEvent, saveMode?: boolean, saveExistingEvent?: boolean): void;
}

export interface UseSavePanelProps {
  event: IEvent;
  saveMode: boolean;
  saveExistingEvent: boolean;
  closeSavePanel: () => void;
  loadedSegments: LoadedSegments | undefined;
  playbackTime: number;
}

export interface UseSavePanel {
  openSavePanel: OpenSavePanelHandler;
  closeSavePanel: () => void;
  savePanelProps: UseSavePanelProps | undefined;
}

/**
 * Helper function for SavePanel which provides a `openSavePanel()` handler to pass
 * to EventGrid.
 *
 * @returns an `{ openSavePanel, closeSavePanel, savePanelProps }` object, where
 *   `savePanelProps` is undefined if the save panel should not be shown.
 * @example
 *     const { openSavePanel, savePanelProps } = useSavePanel(locations);
 *     return savePanelProps && <SavePanel {...savePanelProps} />
 */
export function useSavePanel(): UseSavePanel {
  const [savePanelProps, setSavePanelProps] = useState<UseSavePanelProps | undefined>(undefined);

  const closeSavePanel = useCallback(() => setSavePanelProps(undefined), []);

  const openSavePanel = useCallback(
    (event: IEvent, saveMode: boolean = false, saveExistingEvent: boolean = true) => {
      setSavePanelProps({
        event,
        saveExistingEvent,
        saveMode,
        closeSavePanel,
        loadedSegments: undefined,
        playbackTime: event.startTime.valueOf(),
      });
    },
    [closeSavePanel],
  );

  return {
    openSavePanel,
    closeSavePanel,
    savePanelProps,
  };
}
